import { type EditorPlugin } from './types/aha-editor';
import ListPlugin from './plugins/ListPlugin/ListPlugin';
import BoldPlugin from './plugins/Formatting/BoldPlugin/BoldPlugin';
import ItalicPlugin from './plugins/Formatting/ItalicPlugin/ItalicPlugin';
import UnderlinePlugin from './plugins/Formatting/UnderlinePlugin/UnderlinePlugin';
import StrikethroughPlugin from './plugins/Formatting/StrikethroughPlugin/StrikethroughPlugin';
import CodeSnippetPlugin from './plugins/Formatting/CodeSnippetPlugin/CodeSnippetPlugin';
import MarkdownPlugin from './plugins/MarkdownPlugin/MarkdownPlugin';
import MediaPlugin from './plugins/MediaPlugin/MediaPlugin';
import EmbedPlugin from './plugins/EmbedPlugin/EmbedPlugin';
import LinkPlugin from './plugins/LinkPlugin/LinkPlugin';
import LinkCardPlugin from './plugins/LinkCardPlugin/LinkCardPlugin';
import PasteHtmlPlugin from './plugins/PasteHtmlPlugin/PasteHtmlPlugin';
import DropAttachmentsPlugin from './plugins/DropAttachmentsPlugin/DropAttachmentsPlugin';
import TextColorPlugin from './plugins/Formatting/TextColorPlugin/TextColorPlugin';
import TextHighlightPlugin from './plugins/Formatting/TextHighlightPlugin/TextHighlightPlugin';
import FontSizePlugin from './plugins/Formatting/FontSizePlugin/FontSizePlugin';
import FontFamilyPlugin from './plugins/Formatting/FontFamilyPlugin/FontFamilyPlugin';
import ParagraphPlugin from './plugins/ParagraphPlugin/ParagraphPlugin';
import HeadersPlugin from './plugins/HeadersPlugin/HeadersPlugin';
import QuotePlugin from './plugins/QuotePlugin/QuotePlugin';
import CodeBlockPlugin from './plugins/CodeBlockPlugin/CodeBlockPlugin';
import InfoBlockPlugin from './plugins/InfoBlockPlugin/InfoBlockPlugin';
import LabelPillPlugin from './plugins/LabelPillPlugin/LabelPillPlugin';
import InlinePlugin from './plugins/InlinePlugin/InlinePlugin';
import DividerLinePlugin from './plugins/DividerLinePlugin/DividerLinePlugin';
import ReferenceEmojiPlugin from './plugins/ReferenceEmojiPlugin/ReferenceEmojiPlugin';
import TablePlugin from './plugins/TablePlugin/TablePlugin';
import HotkeysPlugin from './plugins/HotkeysPlugin/HotkeysPlugin';
import PasteMarkdownPlugin from './plugins/PasteMarkdownPlugin/PasteMarkdownPlugin';
import NeverEmptyPlugin from './plugins/NeverEmptyPlugin/NeverEmptyPlugin';
import EscapeToolbarPlugin from './plugins/./EscapeToolbarPlugin/EscapeToolbarPlugin';
import PasteFragmentIntoListPlugin from './plugins/PasteFragmentIntoListPlugin/PasteFragmentIntoListPlugin';
import PasteFragmentIntoTablePlugin from './plugins/PasteFragmentIntoTablePlugin/PasteFragmentIntoTablePlugin';
import AnnotationsPlugin from './plugins/AnnotationsPlugin/AnnotationsPlugin';
import MetadataPlugin from './plugins/MetadataPlugin/MetadataPlugin';
import SharedSnippetPlugin from './plugins/SharedSnippetPlugin/SharedSnippetPlugin';
import PasteFragmentPlugin from './plugins/PasteFragmentPlugin/PasteFragmentPlugin';
import VoidBlockKeyHandlerPlugin from './plugins/VoidBlockKeyHandlerPlugin/VoidBlockKeyHandlerPlugin';
import TranscriptPlugin, {
  TranscriptApiPlugin,
} from './plugins/TranscriptPlugin/TranscriptPlugin';
import HideInlinePlugin from './plugins/HideInlinePlugin/HideInlinePlugin';
import WritingAssistantPlugin from './plugins/WritingAssistantPlugin/WritingAssistantPlugin';
import InfoBlockNormalizationsPlugin from './plugins/InfoBlockPlugin/InfoBlockNormalizationsPlugin';
import TableOfContentsPlugin from './plugins/TableOfContentsPlugin/TableOfContentsPlugin';
import BlurredSelectionPlugin from './plugins/BlurredSelectionPlugin/BlurredSelectionPlugin';
import ReferenceRecordSchemaPlugin from './plugins/ReferencesPlugin/ReferenceRecordSchemaPlugin';
import ReferenceUserSchemaPlugin from './plugins/ReferencesPlugin/ReferenceUserSchemaPlugin';
import ReferenceEmailVariableSchemaPlugin from './plugins/ReferencesPlugin/ReferenceEmailVariableSchemaPlugin';
import MetadataNormalizationPlugin from './plugins/MetadataNormalizationPlugin/MetadataNormalizationPlugin';
import EmbeddedReportSchemaPlugin from './plugins/EmbeddedReportPlugin/EmbeddedReportSchemaPlugin';
import LinkSchemaPlugin from './plugins/LinkPlugin/LinkSchemaPlugin';
import LinkNormalizationPlugin from './plugins/LinkPlugin/LinkNormalization';
import LinkCardSchemaPlugin from './plugins/LinkCardPlugin/LinkCardSchemaPlugin';
import EmbeddedWhiteboardSchemaPlugin from './plugins/EmbeddedWhiteboardPlugin/EmbeddedWhiteboardSchemaPlugin';
import SmoothScrollingPlugin from './plugins/SmoothScrollingPlugin/SmoothScrollingPlugin';
import ElementToolbarPlugin from './plugins/ElementToolbarPlugin/ElementToolbarPlugin';
import WordCountPlugin from './plugins/WordCountPlugin/WordCountPlugin';
import DropPlugin from './plugins/DropPlugin/DropPlugin';
import NestedParagraphsPlugin from './plugins/NestedParagraphsPlugin/NestedParagraphsPlugin';
import PasteFromVSCodePlugin from './plugins/PasteFromVSCodePlugin/PasteFromVSCodePlugin';

// Every available plugin, index so they can be combined for a custom editor.
export const AllPlugins = {
  NeverEmptyPlugin,
  NestedParagraphsPlugin,
  DropAttachmentsPlugin,
  DropPlugin,
  MarkdownPlugin,
  ReferenceEmojiPlugin,
  EmbedPlugin,
  LinkPlugin,
  LinkCardPlugin,
  LinkSchemaPlugin,
  LinkNormalizationPlugin,
  LinkCardSchemaPlugin,
  InlinePlugin,
  BoldPlugin,
  ItalicPlugin,
  UnderlinePlugin,
  StrikethroughPlugin,
  CodeSnippetPlugin,
  ParagraphPlugin,
  HeadersPlugin,
  QuotePlugin,
  CodeBlockPlugin,
  InfoBlockPlugin,
  InfoBlockNormalizationsPlugin,
  ListPlugin,
  LabelPillPlugin,
  MediaPlugin,
  TextColorPlugin,
  TextHighlightPlugin,
  FontSizePlugin,
  FontFamilyPlugin,
  PasteFromVSCodePlugin,
  PasteFragmentIntoTablePlugin,
  PasteFragmentIntoListPlugin,
  PasteFragmentPlugin,
  PasteHtmlPlugin,
  PasteMarkdownPlugin, // Must come after the PasteFragment plugins
  DividerLinePlugin,
  TablePlugin,
  HotkeysPlugin,
  EscapeToolbarPlugin,
  AnnotationsPlugin,
  MetadataPlugin,
  SharedSnippetPlugin,
  VoidBlockKeyHandlerPlugin,
  TranscriptPlugin,
  TranscriptApiPlugin,
  HideInlinePlugin,
  WritingAssistantPlugin,
  TableOfContentsPlugin,
  BlurredSelectionPlugin,
  ReferenceRecordSchemaPlugin,
  ReferenceUserSchemaPlugin,
  MetadataNormalizationPlugin,
  EmbeddedReportSchemaPlugin,
  EmbeddedWhiteboardSchemaPlugin,
  ElementToolbarPlugin,
  WordCountPlugin,
} as const;

export const AllPluginsList: EditorPlugin[] =
  Object.values(AllPlugins).flat(10);

export const UiPlugins = {
  SmoothScrollingPlugin,
} as const;

// Plugins that are essential for basic editor functionality.
export const EssentialPlugins: EditorPlugin[] = [
  InlinePlugin,
  PasteHtmlPlugin,
  PasteMarkdownPlugin,
  HotkeysPlugin,
].flat(10);

// Plugins for the Aha! rich text editor. Should not include plugins for special
// use-case editors.
export const RichTextPlugins: EditorPlugin[] = [
  NeverEmptyPlugin,
  NestedParagraphsPlugin,
  DropAttachmentsPlugin,
  MarkdownPlugin,
  ReferenceEmojiPlugin,
  LinkPlugin,
  LinkCardPlugin,
  InlinePlugin,
  BoldPlugin,
  ItalicPlugin,
  UnderlinePlugin,
  StrikethroughPlugin,
  CodeSnippetPlugin,
  ParagraphPlugin,
  HeadersPlugin,
  QuotePlugin,
  CodeBlockPlugin,
  ListPlugin,
  TextColorPlugin,
  TextHighlightPlugin,
  FontSizePlugin,
  FontFamilyPlugin,
  MediaPlugin, // Must come before PasteHtmlPlugin
  PasteFromVSCodePlugin,
  PasteFragmentIntoTablePlugin,
  PasteFragmentIntoListPlugin,
  PasteFragmentPlugin,
  PasteHtmlPlugin,
  PasteMarkdownPlugin, // Must come after the PasteFragment plugins
  DividerLinePlugin,
  TablePlugin,
  HotkeysPlugin,
  EscapeToolbarPlugin,
  MetadataPlugin,
  SharedSnippetPlugin,
  VoidBlockKeyHandlerPlugin,
  WritingAssistantPlugin,
  BlurredSelectionPlugin,
  ReferenceRecordSchemaPlugin,
  ReferenceUserSchemaPlugin,
  ReferenceEmailVariableSchemaPlugin,
  LinkCardSchemaPlugin,
  MetadataNormalizationPlugin,
  ElementToolbarPlugin,
  WordCountPlugin,
  DropPlugin, // Must be the last plugin that implements onDrop
].flat(10);
